import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Pause, Square, ArrowLeft, CheckCircle, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import ClickableMetric from '@/components/ClickableMetric';
import CompactMetric from '@/components/CompactMetric';
import ZoneIndicator from '@/components/ZoneIndicator';
import { calculateHRZone, maxHrInUse, restHrInUse } from '@/lib/hrZones';
import { useWakeLock } from '@/hooks/useDeviceFeatures';
import { useGPS } from '@/hooks/useGPS';
import { useStrokeDetection } from '@/hooks/useStrokeDetection';
import { useAudioNotifications } from '@/hooks/useAudioNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default bottom bar metrics for program monitor
const DEFAULT_PROGRAM_BOTTOM_METRICS = ['time', 'distance', 'heart_rate', 'calories'];

/**
 * ProgramMonitor - Simplified monitor view for program execution
 * Shows only 2 metric boxes (top row) and current interval info
 */
export default function ProgramMonitor() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Wake Lock - prevents screen from sleeping during program workouts
  useWakeLock();
  
  // GPS - auto-starts with phone GPS fallback
  const gps = useGPS();
  
  // Stroke detection from accelerometer or GPS speed patterns
  const strokeDetection = useStrokeDetection();
  
  // Audio notifications for intervals
  const audio = useAudioNotifications();
  
  // Get program from navigation state or localStorage
  const program = location.state?.program || JSON.parse(localStorage.getItem('active_program') || 'null');
  
  // Session tracking (entire water session)
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const [sessionStartTime] = useState(() => new Date());
  const [sessionData, setSessionData] = useState({
    totalDistance: 0,
    totalCalories: 0,
    heartRateData: [],
    strokeData: [],
    gpsData: []
  });
  
  // Program state
  const [programActive, setProgramActive] = useState(false);
  const [programPaused, setProgramPaused] = useState(false);
  const [programComplete, setProgramComplete] = useState(false);
  const [currentIntervalIndex, setCurrentIntervalIndex] = useState(0);
  const [intervalElapsed, setIntervalElapsed] = useState(0);
  const [intervalDistance, setIntervalDistance] = useState(0);
  
  // Metrics
  const [elapsedTime, setElapsedTime] = useState(0);
  const [distance, setDistance] = useState(0);
  const [strokeRate, setStrokeRate] = useState(0);
  const [heartRate, setHeartRate] = useState(0);
  const [watts, setWatts] = useState(0);
  const [calories, setCalories] = useState(0);
  const [currentSplit, setCurrentSplit] = useState(0);
  const [metersPerSecond, setMetersPerSecond] = useState(0);
  
  // User data
  const [user, setUser] = useState(null);
  const [maxHR, setMaxHR] = useState(185);
  
  // Bottom metrics state
  const [bottomMetrics, setBottomMetrics] = useState(DEFAULT_PROGRAM_BOTTOM_METRICS);
  
  // Refs
  const intervalRef = useRef(null);
  const demoInterval = useRef(null);
  const strokeHistoryRef = useRef([]);
  const intervalsDataRef = useRef([]); // Store data for each completed interval
  const shouldCompleteRef = useRef(false); // Flag to trigger completion
  
  // Demo mode - defaults to OFF
  const [demoMode, setDemoMode] = useState(false);
  
  // Countdown state for program start
  const [countdown, setCountdown] = useState(10); // Start at 10 on page load
  const [countdownFade, setCountdownFade] = useState(false);
  const [countdownComplete, setCountdownComplete] = useState(false);
  
  // Auto-start GPS tracking when program starts
  useEffect(() => {
    if (programActive && !programPaused && !gps.isTracking) {
      // Get GPS preference from localStorage
      const preference = localStorage.getItem('gps_preference') || 'auto';
      
      if (preference === 'phone') {
        gps.usePhoneGPSOnly();
      } else {
        // Auto mode - tries external first, falls back to phone
        gps.startTracking(preference !== 'phone');
      }
    }
    
    // Stop GPS when program ends
    if (!programActive && gps.isTracking) {
      gps.stopTracking();
    }
  }, [programActive, programPaused]);
  
  // Collect GPS data during workout
  useEffect(() => {
    if (gps.position && programActive && !programPaused) {
      setSessionData(prev => ({
        ...prev,
        gpsData: [...prev.gpsData, {
          lat: gps.position.lat,
          lng: gps.position.lng,
          timestamp: Date.now(),
          speed: gps.speed,
          source: gps.source
        }]
      }));
      
      // Update distance from GPS if available
      if (gps.totalDistance > 0) {
        setDistance(prev => Math.max(prev, Math.round(gps.totalDistance)));
      }
      
      // Update speed/split from GPS when available
      if (gps.speed && gps.speed > 0.5) {
        // Convert m/s to split (seconds per 500m)
        const splitFromGPS = 500 / gps.speed;
        setCurrentSplit(splitFromGPS);
        setMetersPerSecond(gps.speed);
      }
    }
  }, [gps.position, gps.totalDistance, gps.speed, programActive, programPaused]);

  // Start/stop stroke detection with program
  useEffect(() => {
    if (programActive && !programPaused && !strokeDetection.isTracking) {
      strokeDetection.startTracking();
    }
    
    if (!programActive && strokeDetection.isTracking) {
      strokeDetection.stopTracking();
    }
  }, [programActive, programPaused]);

  // Update stroke rate and count from detection
  useEffect(() => {
    if (strokeDetection.isTracking) {
      // Only update if we have a valid stroke rate (> 0)
      // This prevents the display from resetting to 0 between strokes
      if (strokeDetection.strokeRate > 0) {
        setStrokeRate(strokeDetection.strokeRate);
      }
    }
  }, [strokeDetection.strokeRate]);

  // Feed GPS speed to stroke detection for fallback
  useEffect(() => {
    if (gps.speed) {
      strokeDetection.updateFromGPSSpeed(gps.speed);
    }
  }, [gps.speed]);

  // Program type colors for countdown
  const getProgramColor = (type) => {
    const colors = {
      interval: '#F97316', // Orange
      pyramid: '#A855F7',  // Purple
      continuous: '#22C55E', // Green
      custom: '#3B82F6'    // Blue
    };
    return colors[type] || colors.custom;
  };
  const countdownColor = getProgramColor(program?.type);

  // Handle bottom metric change
  const handleBottomMetricChange = async (position, metricId) => {
    const newMetrics = [...bottomMetrics];
    newMetrics[position] = metricId;
    setBottomMetrics(newMetrics);
    localStorage.setItem('program_bottom_metrics', JSON.stringify(newMetrics));
    
    // Save to backend
    try {
      await fetch(`${API}/users/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ program_metrics: newMetrics })
      });
    } catch (error) {
      console.log('Failed to save program bottom metric preferences');
    }
  };

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setMaxHR(maxHrInUse(userData));
        }
        
        // Also load bottom metrics preferences
        const prefsResponse = await fetch(`${API}/users/preferences`, { credentials: 'include' });
        if (prefsResponse.ok) {
          const prefs = await prefsResponse.json();
          if (prefs.program_metrics && prefs.program_metrics.length === 4) {
            setBottomMetrics(prefs.program_metrics);
            localStorage.setItem('program_bottom_metrics', JSON.stringify(prefs.program_metrics));
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        // Load from localStorage as fallback
        const saved = localStorage.getItem('program_bottom_metrics');
        if (saved) setBottomMetrics(JSON.parse(saved));
      }
    };
    
    fetchUser();
  }, []);

  // Redirect if no program
  useEffect(() => {
    if (!program) {
      toast.error('No program selected');
      navigate('/programs');
    }
  }, [program, navigate]);

  // Start countdown on page load - auto-starts program when countdown finishes
  useEffect(() => {
    if (!program || countdownComplete) return;
    
    const countdownInterval = setInterval(() => {
      setCountdownFade(true); // Trigger fade animation
      
      setTimeout(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setCountdownFade(false);
            setCountdownComplete(true);
            // Auto-start the program after countdown
            setProgramActive(true);
            setProgramPaused(false);
            setElapsedTime(0);
            setDistance(0);
            // Reset GPS-accumulated distance so the cumulative total runs from
            // this Start to the next Stop.
            try { gps.resetDistance?.(); } catch (_e) { /* noop */ }
            setCalories(0);
            setCurrentIntervalIndex(0);
            setIntervalElapsed(0);
            setIntervalDistance(0);
            strokeHistoryRef.current = [];
            intervalsDataRef.current = [];
            // Play start whistle (if sound enabled for first interval)
            const firstInterval = program?.intervals?.[0];
            if (firstInterval?.soundEnabled !== false) {
              audio.playIntervalStart();
            }
            toast.success(`Starting: ${program.name}`);
            return null;
          }
          setCountdownFade(false); // Reset fade for next number
          // Play countdown beep (except for last one which is handled by start whistle)
          if (prev > 1 && prev <= 3) {
            audio.playCountdownBeep(false);
          }
          return prev - 1;
        });
      }, 400); // Fade duration
    }, 1000);
    
    return () => clearInterval(countdownInterval);
  }, [program, countdownComplete, audio]);

  // Skip countdown and start immediately
  const skipCountdown = () => {
    setCountdown(null);
    setCountdownFade(false);
    setCountdownComplete(true);
    // Start program immediately when skipping
    setProgramActive(true);
    setProgramPaused(false);
    setElapsedTime(0);
    setDistance(0);
    try { gps.resetDistance?.(); } catch (_e) { /* noop */ }
    setCalories(0);
    setCurrentIntervalIndex(0);
    // Play start whistle (if sound enabled for first interval)
    const firstInterval = program?.intervals?.[0];
    if (firstInterval?.soundEnabled !== false) {
      audio.playIntervalStart();
    }
    setIntervalElapsed(0);
    setIntervalDistance(0);
    strokeHistoryRef.current = [];
    intervalsDataRef.current = [];
    toast.success(`Starting: ${program.name}`);
  };

  // Get current interval - use safe access
  const intervals = program?.intervals || [];
  const currentInterval = intervals[currentIntervalIndex];
  const isWorkInterval = currentInterval?.type === 'work';
  const isRestInterval = currentInterval?.type === 'rest';

  // Calculate smoothed M/S
  const calculateSmoothedMPS = (currentDistance) => {
    const now = Date.now();
    strokeHistoryRef.current.push({ distance: currentDistance, timestamp: now });
    
    if (strokeHistoryRef.current.length > 2) {
      strokeHistoryRef.current.shift();
    }
    
    if (strokeHistoryRef.current.length >= 2) {
      const oldest = strokeHistoryRef.current[0];
      const newest = strokeHistoryRef.current[strokeHistoryRef.current.length - 1];
      const distanceDiff = newest.distance - oldest.distance;
      const timeDiff = (newest.timestamp - oldest.timestamp) / 1000;
      
      if (timeDiff > 0) {
        return Math.max(0, distanceDiff / timeDiff);
      }
    }
    return 0;
  };

  // Check interval completion
  useEffect(() => {
    if (!programActive || programPaused || !currentInterval) return;
    
    let shouldAdvance = false;
    
    if (isWorkInterval && currentInterval.distance) {
      // Work interval - check distance
      if (intervalDistance >= currentInterval.distance) {
        shouldAdvance = true;
      }
    } else if (currentInterval.time) {
      // Rest or time-based work interval - check time
      if (intervalElapsed >= currentInterval.time) {
        shouldAdvance = true;
      }
    }
    
    if (shouldAdvance) {
      // Store interval data
      intervalsDataRef.current.push({
        intervalIndex: currentIntervalIndex,
        type: currentInterval.type,
        duration: intervalElapsed,
        distance: intervalDistance,
        avgHeartRate: heartRate,
        avgStrokeRate: strokeRate
      });
      
      // Move to next interval
      if (currentIntervalIndex < intervals.length - 1) {
        // Play interval end whistle (if sound enabled for current interval)
        if (currentInterval?.soundEnabled !== false) {
          audio.playIntervalEnd();
        }
        setCurrentIntervalIndex(prev => prev + 1);
        setIntervalElapsed(0);
        setIntervalDistance(0);
        strokeHistoryRef.current = [];
        toast.success(`Interval ${currentIntervalIndex + 1} complete!`);
        // Play next interval start whistle after short delay (if sound enabled for next interval)
        const nextInterval = intervals[currentIntervalIndex + 1];
        if (nextInterval?.soundEnabled !== false) {
          setTimeout(() => audio.playIntervalStart(), 500);
        }
      } else {
        // Play workout complete whistle (always play for final completion)
        audio.playWorkoutComplete();
        // Set flag to trigger completion
        shouldCompleteRef.current = true;
        setProgramComplete(true);
        setProgramActive(false);
      }
    }
  }, [intervalDistance, intervalElapsed, programActive, programPaused, currentInterval, currentIntervalIndex, intervals, isWorkInterval, isRestInterval, heartRate, strokeRate, audio]);

  // Timer and demo data
  useEffect(() => {
    if (programActive && !programPaused) {
      intervalRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
        setIntervalElapsed(prev => prev + 1);
      }, 1000);
      
      // Demo mode data simulation
      if (demoMode) {
        demoInterval.current = setInterval(() => {
          const baseHr = 150;
          const variation = Math.sin(Date.now() / 5000) * 30;
          setHeartRate(Math.floor(baseHr + variation));
          setStrokeRate(Math.floor(20 + Math.random() * 10));
          
          if (isWorkInterval) {
            setDistance(prev => {
              const newDist = prev + 2.5;
              setIntervalDistance(intDist => intDist + 2.5);
              const mps = calculateSmoothedMPS(newDist);
              setMetersPerSecond(mps);
              return newDist;
            });
            setWatts(Math.floor(200 + Math.random() * 50));
            setCalories(prev => prev + 2);
            setCurrentSplit(120 + Math.random() * 10);
          }
        }, 1000);
      }
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (demoInterval.current) clearInterval(demoInterval.current);
    };
  }, [programActive, programPaused, demoMode, isWorkInterval]);

  // Format helpers
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSplit = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // HR Zone
  const hrZone = calculateHRZone(heartRate, maxHR, restHrInUse(user));

  // Metrics object
  const allMetrics = {
    time: formatTime(elapsedTime),
    stroke_rate: strokeRate,
    heart_rate: heartRate,
    distance: Math.floor(distance),
    watts: watts,
    calories: calories,
    split: formatSplit(currentSplit || 120),
    zone: hrZone ? hrZone.label : 'N/A',
    meters_per_second: metersPerSecond,
    strokes: Math.floor((elapsedTime / 60) * strokeRate), // Estimated strokes
    interval_time: formatTime(intervalElapsed),
    interval_distance: Math.floor(intervalDistance)
  };

  // Start program
  const startProgram = () => {
    setProgramActive(true);
    setProgramPaused(false);
    setElapsedTime(0);
    setDistance(0);
    try { gps.resetDistance?.(); } catch (_e) { /* noop */ }
    setCalories(0);
    setCurrentIntervalIndex(0);
    setIntervalElapsed(0);
    setIntervalDistance(0);
    strokeHistoryRef.current = [];
    intervalsDataRef.current = [];
    toast.success(`Starting: ${program.name}`);
  };

  // Pause/Resume
  const togglePause = () => {
    setProgramPaused(!programPaused);
    toast.info(programPaused ? 'Resumed' : 'Paused');
  };

  // Complete program and save data
  const saveProgramData = async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (demoInterval.current) clearInterval(demoInterval.current);
    
    try {
      // 1. Save to Program History
      const programExecution = {
        program_id: program.id || program.program_id,
        program_name: program.name,
        session_id: sessionId,
        start_time: sessionStartTime.toISOString(),
        end_time: new Date().toISOString(),
        duration: elapsedTime,
        distance: distance,
        avg_stroke_rate: strokeRate,
        avg_heart_rate: heartRate,
        calories: calories,
        intervals_completed: currentIntervalIndex + 1,
        intervals_data: intervalsDataRef.current
      };
      
      const response = await fetch(`${API}/program-executions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(programExecution)
      });
      
      if (response.ok) {
        toast.success('Program complete! Data saved to Program History.');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving program execution:', error);
      toast.error('Error saving program data');
    }
  };

  // Effect to save data when program completes
  useEffect(() => {
    if (programComplete && shouldCompleteRef.current) {
      shouldCompleteRef.current = false;
      saveProgramData();
    }
  }, [programComplete]);

  // Stop and save full session
  const stopSession = async () => {
    // Stop any running intervals
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (demoInterval.current) clearInterval(demoInterval.current);
    
    if (programActive && !programComplete) {
      // Program was stopped early - save partial data
      setProgramActive(false);
      await saveProgramData();
    }
    
    try {
      // 2. Save to Session History (full workout from start to stop)
      const boatType = localStorage.getItem('default_boat_type') || 'c2_erg';
      const sessionWorkout = {
        boat_type: boatType,
        start_time: sessionStartTime.toISOString(),
        end_time: new Date().toISOString(),
        duration: Math.floor((new Date() - sessionStartTime) / 1000),
        distance: distance + sessionData.totalDistance,
        avg_stroke_rate: strokeRate,
        avg_heart_rate: heartRate,
        calories: calories + sessionData.totalCalories,
        program_id: program.id || program.program_id,
        program_name: program.name
      };
      
      const response = await fetch(`${API}/workouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sessionWorkout)
      });
      
      if (response.ok) {
        toast.success('Session saved to Workout History!');
      }
      
      localStorage.removeItem('active_program');
      navigate('/history');
    } catch (error) {
      console.error('Error saving session:', error);
      toast.error('Error saving session');
      navigate('/programs');
    }
  };

  if (!program) {
    return null;
  }

  // Check for valid intervals (already defined above, just check length)
  if (intervals.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-100 dark:bg-zinc-900 flex flex-col items-center justify-center p-4 transition-colors">
        <p className="text-lg text-zinc-600 dark:text-zinc-400 mb-4">This program has no intervals defined.</p>
        <button
          onClick={() => navigate('/programs')}
          className="px-4 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-lg font-bold"
        >
          Back to Programs
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-900 flex flex-col transition-colors">
      {/* Pulsing HR Zone Border */}
      <ZoneIndicator heartRate={heartRate} maxHR={maxHR} />
      
      {/* Countdown Overlay */}
      {countdown !== null && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={skipCountdown}
        >
          <div className="text-center">
            <div 
              className={`text-[20rem] font-black transition-all duration-400 ${
                countdownFade 
                  ? 'opacity-0 scale-150 blur-sm' 
                  : 'opacity-90 scale-100 blur-0'
              }`}
              style={{ 
                fontFamily: 'JetBrains Mono, monospace',
                color: countdownColor,
                textShadow: `0 0 100px ${countdownColor}80, 0 0 200px ${countdownColor}50`,
                lineHeight: 1
              }}
            >
              {countdown}
            </div>
            <div className={`text-2xl font-bold text-white/60 mt-4 uppercase tracking-widest transition-opacity duration-300 ${
              countdownFade ? 'opacity-0' : 'opacity-100'
            }`}>
              {program.name}
            </div>
            <div className="text-sm text-white/40 mt-8">
              Tap anywhere to start now
            </div>
          </div>
        </div>
      )}
      
      <div className="flex-1 p-4">
        <div className="max-w-3xl mx-auto">
          {/* Header with program name */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/programs')}
              className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
              data-testid="back-to-programs"
            >
              <ArrowLeft className="w-6 h-6 text-zinc-600" />
            </button>
            <h1 className="text-xl font-bold text-zinc-800" style={{ fontFamily: 'Archivo, sans-serif' }}>
              {program.name}
            </h1>
            <div className="w-10" /> {/* Spacer */}
          </div>

          {/* Current Interval Info */}
          <div className={`mb-4 p-4 rounded-xl border-2 ${
            isWorkInterval 
              ? 'bg-green-50 border-green-200' 
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <span className={`text-xs font-bold uppercase tracking-wider ${
                  isWorkInterval ? 'text-green-600' : 'text-blue-600'
                }`}>
                  {isWorkInterval ? 'WORK' : 'REST'} - Interval {currentIntervalIndex + 1} of {intervals.length}
                </span>
                <div className="text-2xl font-bold mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {isWorkInterval && currentInterval?.distance ? (
                    <>
                      {Math.floor(intervalDistance)}m / {currentInterval?.distance}m
                    </>
                  ) : (
                    <>
                      {formatTime(intervalElapsed)} / {formatTime(currentInterval?.time || 0)}
                    </>
                  )}
                </div>
              </div>
              {isWorkInterval && currentInterval?.targetPace && (
                <div className="text-right">
                  <span className="text-xs font-bold text-zinc-400">TARGET</span>
                  <div className="text-lg font-bold text-zinc-600">
                    {formatSplit(currentInterval.targetPace)}/500m @ {currentInterval.targetSPM} SPM
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Three Metric Boxes - HR | Split/500m | Stroke Rate - styled like Monitor page */}
          <div className="flex gap-3 mb-6">
            <div className="flex-[0.8] border-2 border-zinc-200 rounded-xl bg-white shadow-sm">
              <ClickableMetric
                metricType="small"
                defaultMetric="heart_rate"
                allMetrics={allMetrics}
                className="p-4 text-center"
                textSize="text-4xl"
                highlight={!!hrZone}
                storageKey="program_metric_1"
              />
            </div>
            <div className="flex-[1.4] border-2 border-zinc-200 rounded-xl bg-white shadow-sm">
              <ClickableMetric
                metricType="small"
                defaultMetric="split"
                allMetrics={allMetrics}
                className="p-6 text-center"
                textSize="text-6xl"
                storageKey="program_metric_2"
              />
            </div>
            <div className="flex-[0.8] border-2 border-zinc-200 rounded-xl bg-white shadow-sm">
              <ClickableMetric
                metricType="small"
                defaultMetric="stroke_rate"
                allMetrics={allMetrics}
                className="p-4 text-center"
                textSize="text-4xl"
                storageKey="program_metric_3"
              />
            </div>
          </div>

          {/* Session Stats - Clickable metrics with simplified labels */}
          <div className="mb-6 border-2 border-zinc-200 rounded-xl py-3 px-2 bg-white">
            <div className="flex justify-center items-center divide-x divide-zinc-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {bottomMetrics.map((metricId, index) => (
                <CompactMetric
                  key={`program-bottom-${index}`}
                  metricId={metricId}
                  allMetrics={allMetrics}
                  position={index}
                  onChangeMetric={handleBottomMetricChange}
                />
              ))}
            </div>
          </div>

          {/* GPS Status Indicator */}
          {(gps.hasGPS || programActive) && (
            <div className="flex justify-center mb-4">
              {gps.hasGPS ? (
                <div 
                  className={`px-3 py-1.5 font-bold tracking-wide text-xs rounded-lg shadow-sm flex items-center gap-1.5 ${
                    gps.isPhoneGPS 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-green-500 text-white'
                  }`}
                >
                  <MapPin className="w-3 h-3" />
                  {gps.isPhoneGPS ? 'Phone GPS' : 'CREWZ GPS'}
                  {gps.accuracy && <span className="opacity-75">±{Math.round(gps.accuracy)}m</span>}
                  {gps.speed > 0 && <span className="opacity-75">• {gps.speed.toFixed(2)} m/s</span>}
                </div>
              ) : (
                <div className="px-3 py-1.5 bg-zinc-300 text-zinc-600 font-bold tracking-wide text-xs rounded-lg shadow-sm flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" />
                  GPS Starting...
                </div>
              )}
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex justify-center items-center gap-6">
            {!programActive && !programComplete && countdown === null && (
              <button
                onClick={startProgram}
                className="w-20 h-20 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105"
                data-testid="start-program-btn"
              >
                <Play className="w-10 h-10 text-white ml-1" fill="white" />
              </button>
            )}
            
            {programActive && (
              <>
                <button
                  onClick={togglePause}
                  className={`w-16 h-16 ${programPaused ? 'bg-green-500 hover:bg-green-600' : 'bg-yellow-500 hover:bg-yellow-600'} rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105`}
                  data-testid="pause-program-btn"
                >
                  {programPaused ? (
                    <Play className="w-8 h-8 text-white ml-0.5" fill="white" />
                  ) : (
                    <Pause className="w-8 h-8 text-white" fill="white" />
                  )}
                </button>
                
                <button
                  onClick={stopSession}
                  className="w-20 h-20 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105"
                  data-testid="stop-session-btn"
                >
                  <Square className="w-10 h-10 text-white" fill="white" />
                </button>
              </>
            )}
            
            {programComplete && (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-xl font-bold text-zinc-700 mb-4">Program Complete!</p>
                <button
                  onClick={stopSession}
                  className="px-6 py-3 bg-zinc-900 text-white rounded-lg font-bold hover:bg-zinc-800 transition-colors"
                  data-testid="finish-session-btn"
                >
                  Finish & Save Session
                </button>
              </div>
            )}
          </div>

          {/* Demo Mode Toggle */}
          <div className="mt-6 text-center">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={demoMode}
                onChange={(e) => setDemoMode(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-10 h-6 rounded-full transition-colors ${demoMode ? 'bg-green-500' : 'bg-zinc-300'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${demoMode ? 'translate-x-4' : 'translate-x-0.5'} mt-0.5`} />
              </div>
              <span className="text-sm text-zinc-500">Demo Mode</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
